import { useRef } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { useInView, useReducedMotion } from 'motion/react'
import { cn } from '@/lib/utils'
import {
  BandReveal,
  HeroReveal,
  InteractiveDots,
  Pressable,
  ScrollReveal,
  StaggerContainer,
  StaggerItem,
  TextReveal,
} from '@/components/motion'
import { DocThumb, RotatedProcessCards, ServiceFaq } from '@/components/landing/service'
import { Highlight } from '@/components/landing/about/Highlight'
import type { SolutionData } from '@/data/solutions'
import { StepRows } from './StepRows'
import { ToneScrollCards } from './ToneScrollCards'
import { VarianceSheet } from './VarianceSheet'
import { FADED, QUOTE, SolutionCta, SolutionStoryCard, splitLastWord, withHighlights, type SolutionLayoutProps } from './shared'

const DOC_ACCENTS = ['green', 'blue', 'purple', 'pink'] as const

function DocItem({ doc, index }: { doc: SolutionData['documents'][number]; index: number }) {
  return (
    <span className="flex shrink-0 items-center gap-4">
      <DocThumb kind={doc.kind} title={doc.title} accent={DOC_ACCENTS[index % DOC_ACCENTS.length]} className="aspect-[3/4] w-12" />
      <span className="font-[family-name:var(--font-landing-subdisplay)] text-xl font-semibold tracking-[-0.01em] text-[var(--color-landing-gray-900)] dark:text-white md:text-2xl">
        {doc.title}
      </span>
    </span>
  )
}

/**
 * The about page's strip (.25fr / 1fr): a faded label with the team name,
 * and a 24s marquee of the documents the team builds, with faded edges.
 * Pauses off-screen; a wrapped static list under reduced motion.
 */
function DocumentStrip({ solution }: { solution: SolutionData }) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref)
  const reduce = useReducedMotion()
  // Two passes per copy so one copy is always wider than the column.
  const docs = [...solution.documents, ...solution.documents]

  return (
    <section className="landing-section-sm">
      <div className="landing-container grid items-center gap-6 lg:grid-cols-[minmax(0,0.25fr)_minmax(0,1fr)] lg:gap-12">
        <p className={cn('landing-text-body-lg max-w-[26ch]', FADED)}>
          <span className="block font-semibold text-[var(--color-landing-gray-900)] dark:text-white">{solution.teamLabel}</span>{' '}
          <span className="block">The documents Templatean builds with you.</span>
        </p>

        {reduce ? (
          <ul className="flex flex-wrap gap-x-10 gap-y-5">
            {solution.documents.map((doc, i) => (
              <li key={doc.title}>
                <DocItem doc={doc} index={i} />
              </li>
            ))}
          </ul>
        ) : (
          <div ref={ref} className="overflow-hidden py-2 [mask-image:linear-gradient(to_right,transparent,#000_12%,#000_88%,transparent)]">
            <ul className="sr-only">
              {solution.documents.map((doc) => (
                <li key={doc.title}>{doc.title}</li>
              ))}
            </ul>
            <div
              aria-hidden
              className="animate-marquee flex w-max"
              style={{ animationDuration: '24s', animationPlayState: inView ? 'running' : 'paused' }}
            >
              {[0, 1].map((copy) => (
                <div key={copy} className="flex shrink-0 items-center gap-14 pr-14">
                  {docs.map((doc, i) => (
                    <DocItem key={`${copy}-${i}`} doc={doc} index={i} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

/**
 * Statement layout (blue2purple /about), used by Finance: a centred statement
 * hero with inline highlight boxes, a 16:9 animated variance sheet with the
 * stats under it, a strip of the team's documents, the features as rotated
 * cards on a pink band, the steps as large hover rows, the challenge as
 * purple scroll cards, FAQ with the story, and a pink CTA band.
 */
export function StatementLayout({ solution, story }: SolutionLayoutProps) {
  const [featLead, featLast] = splitLastWord(solution.featuresHeadline)

  return (
    <>
      {/* 1. Statement hero: centred 60rem block, quote + button row 6rem apart */}
      <section className="pb-[clamp(3rem,7vh,5rem)] pt-[clamp(4rem,12vh,8rem)]">
        <div className="landing-container">
          <div className="mx-auto max-w-[60rem]">
            <HeroReveal delay={0.05}>
              <h1 className="landing-text-display-xl text-[var(--color-landing-gray-900)] dark:text-white">
                {withHighlights(solution.headline, solution.headlineHighlights, ['pink', 'blue'])}
              </h1>
            </HeroReveal>
            <HeroReveal delay={0.2}>
              <div className="mt-12 flex flex-col gap-10 lg:mt-16 lg:flex-row lg:items-center lg:justify-between lg:gap-24">
                <p className={cn(QUOTE, 'max-w-[56ch] text-pretty')}>{solution.subheadline}</p>
                <Pressable className="shrink-0 self-start lg:self-auto">
                  <Link to="/contact" className="landing-cta landing-cta--solid landing-cta--lg landing-btn-sweep">
                    Book a demo
                    <ArrowRight aria-hidden className="h-5 w-5" />
                  </Link>
                </Pressable>
              </div>
            </HeroReveal>
          </div>
        </div>
      </section>

      {/* 2. Media: animated variance sheet, stats under it */}
      <section aria-label={`${solution.teamName} in numbers`} className="landing-container">
        <HeroReveal delay={0.3} distance={40}>
          <VarianceSheet title="Budget vs actual" />
        </HeroReveal>
        <StaggerContainer
          amount={0.3}
          className="grid border-b border-[var(--color-landing-gray-200)] dark:border-[var(--color-landing-gray-800)] sm:grid-cols-3"
        >
          {solution.heroStats.map((stat) => (
            <StaggerItem
              key={stat.label}
              className="border-t border-[var(--color-landing-gray-200)] py-8 dark:border-[var(--color-landing-gray-800)] sm:border-l sm:px-8 sm:first:border-l-0 sm:first:pl-0 md:py-10"
            >
              <p className="whitespace-nowrap font-[family-name:var(--font-landing-display)] text-[clamp(2.5rem,4.6vw,4.5rem)] uppercase leading-[1.2] tracking-[-0.02em] text-[var(--color-landing-gray-900)] tabular-nums dark:text-white">
                {stat.value}
              </p>
              <p className={cn('mt-3 text-lg font-bold uppercase leading-snug', FADED)}>{stat.label}</p>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </section>

      {/* 3. Strip: the documents the team builds */}
      <DocumentStrip solution={solution} />

      {/* 4. Features: rotated process cards on a pink band */}
      <BandReveal>
        <section aria-labelledby="fin-features-title" className="landing-section-lg relative overflow-x-clip bg-[#ff00f2]">
          <InteractiveDots />
          <div className="landing-container relative z-10">
            <div className="grid gap-10 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] lg:items-end lg:gap-20">
              <TextReveal as="h2" className="landing-text-display-xl text-white">
                <span id="fin-features-title">
                  {featLead} <Highlight color="blue">{featLast}</Highlight>
                </span>
              </TextReveal>
              <ScrollReveal direction="none" delay={0.15}>
                <p className="landing-text-body-lg max-w-[48ch] border-l border-white/60 pl-5 text-white">{solution.featuresDescription}</p>
                <Pressable className="mt-8 inline-block">
                  <Link
                    to="/register"
                    className="landing-cta landing-cta--solid landing-cta--lg landing-cta--on-color landing-btn-sweep border-[var(--color-landing-accent)] bg-[var(--color-landing-accent)] dark:border-[var(--color-landing-accent)] dark:bg-[var(--color-landing-accent)] dark:text-white"
                  >
                    Start free trial
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </Pressable>
              </ScrollReveal>
            </div>
            <RotatedProcessCards
              className="mt-16 lg:mt-10"
              cards={solution.features.map((f) => ({ title: f.title, description: f.description, outcomes: f.outcomes }))}
            />
          </div>
        </section>
      </BandReveal>

      {/* 5. Steps: large hover rows */}
      <StepRows solution={solution} />

      {/* 6. The challenge: scroll cards on purple */}
      <ToneScrollCards
        tone="purple"
        heading={solution.problemHeadline}
        body={solution.problemDescription}
        cta={{ label: 'Book a demo', href: '/contact' }}
        documents={solution.documents}
        cards={solution.problemPoints}
      />

      {/* 7. FAQ + featured story */}
      <ServiceFaq items={solution.faq} aside={<SolutionStoryCard solution={solution} story={story} />} />

      {/* 8. Pink CTA band */}
      <SolutionCta solution={solution} tone="pink" />
    </>
  )
}

export default StatementLayout
